import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/api.service';
import { DashboardPecasDia, DashboardRankingResponsavel, DashboardRecebimentoDia, DashboardRecebimentoRealizadoDia, DashboardResponsavelDetalhe, DashboardResponsavelReceber, DashboardResumo } from '../../core/models';

@Component({
  standalone: true,
  imports: [DecimalPipe, NgFor, NgIf, FormsModule],
  template: `
    <header class="page-header">
      <div>
        <h2>Dashboard</h2>
        <p>Resumo operacional do dia</p>
      </div>
      <a class="primary-link" href="/contagens/nova">Nova contagem</a>
    </header>

    <section class="metrics">
      <article><span>Contagens do dia</span><strong>{{ resumo.total_contagens_dia || 0 }}</strong></article>
      <article><span>Peças contadas hoje</span><strong>{{ resumo.total_pecas_dia || 0 | number }}</strong></article>
      <article><span>Contagens abertas</span><strong>{{ resumo.contagens_abertas || 0 }}</strong></article>
      <article><span>Finalizadas</span><strong>{{ resumo.contagens_finalizadas || 0 }}</strong></article>
      <article><span>A receber</span><strong>{{ formatMoeda(totalAReceber) }}</strong></article>
    </section>

    <section class="dashboard-receivable-layout">
      <article class="panel dashboard-ranking dashboard-counts-chart">
        <header>
          <h3>Peças por dia</h3>
          <span>Últimos 15 dias</span>
        </header>

        <div class="daily-counts-chart" *ngIf="pecasUltimosDias.length; else semPecasRecentes">
          <div class="daily-count-column" *ngFor="let item of pecasUltimosDias">
            <strong>{{ item.total_pecas | number }}</strong>
            <div class="daily-count-track">
              <div class="daily-count-bar" [style.height.%]="alturaPecaDia(item)"></div>
            </div>
            <span>{{ formatDiaCurto(item.data) }}</span>
          </div>
        </div>
        <ng-template #semPecasRecentes><p class="field-hint">Nenhuma peça nos últimos 15 dias.</p></ng-template>
      </article>

      <article class="panel dashboard-ranking dashboard-receivable">
        <header>
          <h3>Responsáveis a receber</h3>
          <span>{{ formatMoeda(totalAReceber) }}</span>
        </header>

        <table class="data-table ranking-table receivable-table" *ngIf="responsaveisAReceber.length; else semRecebimentos">
          <thead><tr><th>Responsável</th><th>Pagamentos pendentes</th><th>Pix pendente</th><th>Total</th></tr></thead>
          <tbody>
            <tr *ngFor="let item of responsaveisAReceber; let i = index"
                class="is-clickable"
                role="button"
                tabindex="0"
                [attr.aria-label]="'Ver detalhamento dos pagamentos de ' + item.responsavel"
                title="Ver detalhamento"
                (click)="abrirDetalhamento(item)"
                (keydown.enter)="abrirDetalhamento(item)"
                (keydown.space)="abrirDetalhamento(item); $event.preventDefault()">
              <td data-label="Responsável"><b>{{ i + 1 }}</b>{{ item.responsavel }}</td>
              <td data-label="Pagamentos pendentes">
                {{ formatMoeda(item.total_pendente) }}
                <span class="muted-line">{{ item.pagamentos_pendentes || 0 }} lançamento(s)</span>
              </td>
              <td data-label="Pix pendente">
                {{ formatMoeda(item.total_pix_pendente) }}
                <span class="muted-line">{{ item.pix_pendentes || 0 }} Pix</span>
              </td>
              <td data-label="Total"><strong>{{ formatMoeda(item.total_receber) }}</strong></td>
            </tr>
          </tbody>
        </table>
        <ng-template #semRecebimentos><p class="field-hint">Nenhum valor pendente de pagamento ou Pix.</p></ng-template>
      </article>
    </section>

    <div class="modal-backdrop" *ngIf="detalhamentoAberto" (click)="fecharDetalhamento($event)">
      <section class="modal receivable-detail-modal" role="dialog" aria-modal="true" aria-labelledby="titulo-detalhamento-responsavel">
        <header class="modal-header">
          <div>
            <h3 id="titulo-detalhamento-responsavel">Recebimentos por dia</h3>
            <p *ngIf="detalhamentoResponsavel">Responsável: {{ detalhamentoResponsavel.responsavel }}</p>
            <p *ngIf="carregandoDetalhamento">Carregando os lançamentos...</p>
          </div>
          <button class="ghost modal-close" type="button" (click)="fecharDetalhamento()" aria-label="Fechar">X</button>
        </header>

        <p class="field-hint" *ngIf="erroDetalhamento">Não foi possível carregar o detalhamento. Tente novamente.</p>

        <ng-container *ngIf="!carregandoDetalhamento && detalhamentoResponsavel as detalhe">
          <div class="receivable-detail-summary">
            <article><span>Pagamento pendente</span><strong>{{ formatMoeda(detalhe.total_pendente) }}</strong></article>
            <article><span>Pix pendente</span><strong>{{ formatMoeda(detalhe.total_pix_pendente) }}</strong></article>
            <article><span>Total a receber</span><strong>{{ formatMoeda(detalhe.total_receber) }}</strong></article>
            <article><span>Valores pagos no mês</span><strong>{{ formatMoeda(totalPagoMesSelecionado) }}</strong></article>
          </div>

          <div class="receivable-detail-list">
            <section class="receivable-daily-section" *ngIf="recebimentosPendentes.length">
              <h4>Retirado e pendente</h4>
              <div class="table-scroll">
                <table class="data-table receivable-detail-table receivable-daily-table">
                  <thead><tr><th>Data</th><th>{{ detalhe.responsavel }}</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let recebimento of recebimentosPendentes">
                      <td data-label="Data">{{ formatData(recebimento.data) }}</td>
                      <td data-label="A receber">{{ formatMoeda(recebimento.total_pendente) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr><th>Total pendente</th><th>{{ formatMoeda(detalhe.total_pendente) }}</th></tr>
                  </tfoot>
                </table>
              </div>
            </section>

            <section class="receivable-daily-section" *ngIf="recebimentosPixPendentes.length">
              <h4>Pix pendente</h4>
              <div class="table-scroll">
                <table class="data-table receivable-detail-table receivable-daily-table">
                  <thead><tr><th>Data</th><th>{{ detalhe.responsavel }}</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let recebimento of recebimentosPixPendentes">
                      <td data-label="Data">{{ formatData(recebimento.data) }}</td>
                      <td data-label="Pix pendente">{{ formatMoeda(recebimento.total_pix_pendente) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr><th>Total Pix pendente</th><th>{{ formatMoeda(detalhe.total_pix_pendente) }}</th></tr>
                  </tfoot>
                </table>
              </div>
            </section>

            <section class="receivable-daily-section">
              <header class="receivable-daily-heading">
                <h4>Valores pagos</h4>
                <label class="receivable-month-filter">
                  <span>Mês</span>
                  <select
                    name="mesPagamentoDashboard"
                    [(ngModel)]="mesSelecionado"
                    aria-label="Filtrar pagamentos pelo mês"
                  >
                    <option *ngFor="let mes of opcoesMesPagamento" [value]="mes.valor">{{ mes.label }}</option>
                  </select>
                </label>
              </header>
              <div class="table-scroll">
                <table class="data-table receivable-detail-table receivable-daily-table">
                  <thead><tr><th>Data do pagamento</th><th>{{ detalhe.responsavel }}</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let recebimento of recebimentosRealizados">
                      <td data-label="Data do pagamento">{{ formatData(recebimento.data) }}</td>
                      <td data-label="Pago">{{ formatMoeda(recebimento.total) }}</td>
                    </tr>
                    <tr *ngIf="!recebimentosRealizados.length">
                      <td colspan="2" class="field-hint">Nenhum valor pago neste mês.</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr><th>Total pago no mês</th><th>{{ formatMoeda(totalPagoMesSelecionado) }}</th></tr>
                  </tfoot>
                </table>
              </div>
            </section>
          </div>

          <div class="receivable-detail-list" *ngIf="false">
            <article class="receivable-detail-payment" *ngFor="let pagamento of []">
              <header>
                <div>
                  <strong>{{ pagamento.numero_documento || pagamento.numero_pagamento || 'Lançamento de produção' }}</strong>
                  <span>{{ pagamento.cliente || 'Cliente não informado' }}</span>
                </div>
                <span class="receivable-detail-status">{{ pagamento.status_label }}</span>
              </header>

              <div class="receivable-detail-meta">
                <span *ngIf="pagamento.tipo_documento">{{ pagamento.tipo_documento }}</span>
                <span *ngIf="pagamento.numero_pagamento">{{ pagamento.numero_pagamento }}</span>
                <span *ngIf="pagamento.data_retirada">Retirada: {{ formatData(pagamento.data_retirada) }}</span>
                <span *ngIf="pagamento.data_pagamento">Pagamento: {{ formatData(pagamento.data_pagamento) }}</span>
              </div>

              <div class="table-scroll">
                <table class="data-table receivable-detail-table">
                  <thead><tr><th>Produção</th><th>Quant.</th><th>Valor</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let producao of pagamento.producoes">
                      <td data-label="Produção">{{ producao.codigo_produto ? producao.codigo_produto + ' - ' : '' }}{{ producao.produto || 'Produto não informado' }}{{ producao.tamanho ? ' / ' + producao.tamanho : '' }}</td>
                      <td data-label="Quant.">{{ producao.quantidade | number }} ({{ producao.responsaveis_calculo }} resp.)</td>
                      <td data-label="Valor">{{ formatMoeda(producao.valor) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="receivable-detail-adjustments" *ngIf="pagamento.ajustes.length">
                <div *ngFor="let ajuste of pagamento.ajustes">
                  <span>{{ ajuste.descricao }}</span>
                  <strong [class.is-discount]="ajuste.tipo === 'DESCONTO'">{{ ajuste.tipo === 'DESCONTO' ? '-' : '+' }} {{ formatMoeda(ajuste.valor) }}</strong>
                </div>
              </div>

              <footer>
                <span>Subtotal: {{ formatMoeda(pagamento.subtotal) }}</span>
                <strong>Total: {{ formatMoeda(pagamento.total) }}</strong>
              </footer>
            </article>
          </div>
        </ng-container>
      </section>
    </div>

    <section class="dashboard-rankings">
      <article class="panel dashboard-ranking">
        <header>
          <h3>Ranking de contagens do dia</h3>
          <span>{{ totalRankingDia | number }} peças</span>
        </header>

        <table class="data-table ranking-table" *ngIf="rankingDia.length; else semRankingDia">
          <thead><tr><th>Responsável</th><th>Contagens</th><th>Peças</th></tr></thead>
          <tbody>
            <tr *ngFor="let item of rankingDia; let i = index">
              <td data-label="Responsável"><b>{{ i + 1 }}</b>{{ item.responsavel }}</td>
              <td data-label="Contagens">{{ item.total_contagens }}</td>
              <td data-label="Peças">{{ item.total_pecas | number }}</td>
            </tr>
          </tbody>
        </table>
        <ng-template #semRankingDia><p class="field-hint">Nenhuma contagem registrada hoje</p></ng-template>
      </article>

      <article class="panel dashboard-ranking">
        <header>
          <h3>Ranking de contagens do mês</h3>
          <span>{{ totalRankingMes | number }} peças</span>
        </header>

        <table class="data-table ranking-table" *ngIf="rankingMes.length; else semRankingMes">
          <thead><tr><th>Responsável</th><th>Contagens</th><th>Peças</th></tr></thead>
          <tbody>
            <tr *ngFor="let item of rankingMes; let i = index">
              <td data-label="Responsável"><b>{{ i + 1 }}</b>{{ item.responsavel }}</td>
              <td data-label="Contagens">{{ item.total_contagens }}</td>
              <td data-label="Peças">{{ item.total_pecas | number }}</td>
            </tr>
          </tbody>
        </table>
        <ng-template #semRankingMes><p class="field-hint">Nenhuma contagem registrada neste mês</p></ng-template>
      </article>

      <article class="panel dashboard-ranking dashboard-ranking-period">
        <header>
          <h3>Ranking por período</h3>
          <span>{{ totalRankingPeriodo | number }} peças</span>
        </header>

        <form class="ranking-period-form" (ngSubmit)="aplicarRankingPeriodo()">
          <label>Data inicial<input type="date" name="dataInicialRanking" [(ngModel)]="dataInicialRanking"></label>
          <label>Data final<input type="date" name="dataFinalRanking" [(ngModel)]="dataFinalRanking"></label>
          <div class="ranking-period-actions">
            <button type="submit">Aplicar</button>
            <button class="ghost" type="button" (click)="limparRankingPeriodo()">Limpar</button>
          </div>
        </form>

        <p class="field-hint" *ngIf="periodoSelecionado">Período selecionado: {{ periodoSelecionado }}</p>

        <table class="data-table ranking-table" *ngIf="rankingPeriodo.length; else semRankingPeriodo">
          <thead><tr><th>Responsável</th><th>Contagens</th><th>Peças</th></tr></thead>
          <tbody>
            <tr *ngFor="let item of rankingPeriodo; let i = index">
              <td data-label="Responsável"><b>{{ i + 1 }}</b>{{ item.responsavel }}</td>
              <td data-label="Contagens">{{ item.total_contagens }}</td>
              <td data-label="Peças">{{ item.total_pecas | number }}</td>
            </tr>
          </tbody>
        </table>
        <ng-template #semRankingPeriodo><p class="field-hint">Selecione uma data inicial e final para consultar o ranking</p></ng-template>
      </article>
    </section>
  `
})
export class HomeComponent implements OnInit {
  private api = inject(ApiService);
  dataInicialRanking = '';
  dataFinalRanking = '';
  mesSelecionado = '';
  detalhamentoResponsavel: DashboardResponsavelDetalhe | null = null;
  carregandoDetalhamento = false;
  erroDetalhamento = false;

  get detalhamentoAberto() {
    return this.carregandoDetalhamento || !!this.detalhamentoResponsavel || this.erroDetalhamento;
  }
  resumo: DashboardResumo = {
    total_contagens_dia: 0,
    total_pecas_dia: 0,
    total_pecas_mes: 0,
    total_pecas_periodo: 0,
    contagens_abertas: 0,
    contagens_finalizadas: 0,
    ranking_contagens_dia: [],
    ranking_contagens_mes: [],
    ranking_contagens_periodo: [],
    pecas_ultimos_dias: [],
    responsaveis_a_receber: []
  };

  ngOnInit() {
    this.carregarDashboard();
  }

  carregarDashboard(filtros: Record<string, string> = {}) {
    this.api.dashboard(filtros).subscribe((resumo) => (this.resumo = resumo));
  }

  aplicarRankingPeriodo() {
    this.carregarDashboard({
      data_inicial: this.dataInicialRanking,
      data_final: this.dataFinalRanking
    });
  }

  limparRankingPeriodo() {
    this.dataInicialRanking = '';
    this.dataFinalRanking = '';
    this.carregarDashboard();
  }

  abrirDetalhamento(item: DashboardResponsavelReceber) {
    this.mesSelecionado = this.chaveMesAtual;
    this.detalhamentoResponsavel = null;
    this.erroDetalhamento = false;
    this.carregandoDetalhamento = true;
    this.api.dashboardResponsavelDetalhe(item.responsavel).subscribe({
      next: (detalhe) => {
        this.detalhamentoResponsavel = detalhe;
        this.carregandoDetalhamento = false;
      },
      error: () => {
        this.erroDetalhamento = true;
        this.carregandoDetalhamento = false;
      }
    });
  }

  fecharDetalhamento(event?: Event) {
    if (event && event.target !== event.currentTarget) return;
    this.detalhamentoResponsavel = null;
    this.carregandoDetalhamento = false;
    this.erroDetalhamento = false;
  }

  get rankingDia(): DashboardRankingResponsavel[] {
    return this.resumo.ranking_contagens_dia || [];
  }

  get rankingMes(): DashboardRankingResponsavel[] {
    return this.resumo.ranking_contagens_mes || [];
  }

  get rankingPeriodo(): DashboardRankingResponsavel[] {
    return this.resumo.ranking_contagens_periodo || [];
  }

  get responsaveisAReceber(): DashboardResponsavelReceber[] {
    return this.resumo.responsaveis_a_receber || [];
  }

  get pecasUltimosDias(): DashboardPecasDia[] {
    return this.resumo.pecas_ultimos_dias || [];
  }

  get maxPecasUltimosDias() {
    return Math.max(...this.pecasUltimosDias.map((item) => Number(item.total_pecas || 0)), 1);
  }

  alturaPecaDia(item: DashboardPecasDia) {
    return Math.max((Number(item.total_pecas || 0) / this.maxPecasUltimosDias) * 100, 8);
  }

  get recebimentosPorDia(): DashboardRecebimentoDia[] {
    return this.detalhamentoResponsavel?.recebimentos_por_dia || [];
  }

  get recebimentosPendentes(): DashboardRecebimentoDia[] {
    return this.recebimentosPorDia.filter((recebimento) => Number(recebimento.total_pendente || 0) > 0);
  }

  get recebimentosPixPendentes(): DashboardRecebimentoDia[] {
    return this.recebimentosPorDia.filter((recebimento) => Number(recebimento.total_pix_pendente || 0) > 0);
  }

  get recebimentosRealizados(): DashboardRecebimentoRealizadoDia[] {
    const mesSelecionado = this.mesSelecionado || this.chaveMesAtual;
    return (this.detalhamentoResponsavel?.recebimentos_realizados_por_dia || [])
      .filter((recebimento) => String(recebimento.data || '').slice(0, 7) === mesSelecionado);
  }

  get chaveMesAtual() {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  }

  get opcoesMesPagamento() {
    const meses = new Set<string>([this.chaveMesAtual]);
    for (const recebimento of this.detalhamentoResponsavel?.recebimentos_realizados_por_dia || []) {
      const mes = String(recebimento.data || '').slice(0, 7);
      if (/^\d{4}-(0[1-9]|1[0-2])$/.test(mes)) meses.add(mes);
    }

    return [...meses]
      .sort((a, b) => b.localeCompare(a))
      .map((valor) => {
        const [ano, mes] = valor.split('-').map(Number);
        const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
          .format(new Date(ano, mes - 1, 1));
        return { valor, label: label.charAt(0).toUpperCase() + label.slice(1) };
      });
  }

  get totalPagoMesSelecionado() {
    return this.recebimentosRealizados.reduce((total, recebimento) => total + Number(recebimento.total || 0), 0);
  }

  get totalAReceber() {
    return this.resumo.total_a_receber ?? this.responsaveisAReceber.reduce((sum, item) => sum + Number(item.total_receber || 0), 0);
  }

  get totalRankingDia() {
    return Number(this.resumo.total_pecas_dia || 0);
  }

  get totalRankingMes() {
    return this.resumo.total_pecas_mes ?? this.rankingMes.reduce((sum, item) => sum + Number(item.total_pecas || 0), 0);
  }

  get totalRankingPeriodo() {
    return this.resumo.total_pecas_periodo ?? this.rankingPeriodo.reduce((sum, item) => sum + Number(item.total_pecas || 0), 0);
  }

  get periodoSelecionado() {
    if (!this.dataInicialRanking || !this.dataFinalRanking) return '';
    return `${this.formatDate(this.dataInicialRanking)} a ${this.formatDate(this.dataFinalRanking)}`;
  }

  private formatDate(value: string) {
    const [ano, mes, dia] = value.split('-');
    return ano && mes && dia ? `${dia}/${mes}/${ano}` : value;
  }

  formatData(value: string | null | undefined) {
    if (!value) return '-';
    const data = String(value).slice(0, 10);
    return this.formatDate(data);
  }

  formatDiaCurto(value: string | null | undefined) {
    if (!value) return '-';
    const data = String(value).slice(0, 10);
    const [ano, mes, dia] = data.split('-');
    return ano && mes && dia ? `${dia}/${mes}` : data;
  }

  formatMoeda(value: string | number | null | undefined) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}
